// @spec IAM-STR-021
// @spec IAM-STR-022
// @spec SHL-STR-005
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateRoleDialog, RolesList } from '@/components/roles';
import { Button } from '@/components/ui/button';
import { useOrgRole } from '@/hooks/use-org-role';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/roles')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.roles'),
  }),
  component: RolesPage,
});

/**
 * Roles page component.
 *
 * Displays all roles (system and custom) for the current organization.
 * Allows creating custom roles for users with org:roles:create permission.
 * Requires org:roles:read permission.
 */
function RolesPage() {
  const { organizations } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const { isAdmin, isAdminOrManager } = useOrgRole();
  const { t } = useTranslation('settings');
  const canViewRoles = isAdminOrManager;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const canCreateRole = isAdmin && Boolean(activeOrg?.id);

  if (!canViewRoles) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">
              {t('rolesPage.accessDeniedTitle')}
            </h2>
            <p className="mt-2 text-muted-foreground">{t('rolesPage.accessDeniedDescription')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('rolesPage.title')}</h1>
            <p className="mt-1 text-muted-foreground">{t('rolesPage.description')}</p>
          </div>

          {canCreateRole && (
            <Button
              onClick={() => {
                setIsCreateDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('rolesPage.createCustomRole')}
            </Button>
          )}
        </header>

        <RolesList orgId={activeOrg?.id} />

        {activeOrg?.id && (
          <CreateRoleDialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
            orgId={activeOrg.id}
          />
        )}
      </div>
    </div>
  );
}
